import React from 'react';
import {
  XMarkIcon,
  UserIcon,
  EnvelopeIcon,
  ShieldCheckIcon,
  CalendarIcon,
} from '@heroicons/react/24/outline';

const UserDetailsModal = ({ isOpen, onClose, user, users }) => {
  if (!isOpen || !user) return null;

  const selectedUser = users.find((u) => u.id === user);

  if (!selectedUser) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose}></div>
      <div className="relative w-full max-w-2xl rounded-lg bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">User Details</h2>
          <button
            onClick={onClose}
            className="rounded-md p-1 text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <UserIcon className="h-4 w-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-700">Name:</span>
                <span className="text-sm text-gray-900">
                  {selectedUser.first_name} {selectedUser.last_name}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <EnvelopeIcon className="h-4 w-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-700">Email:</span>
                <span className="text-sm text-gray-900">{selectedUser.email}</span>
              </div>
              <div className="flex items-center gap-2">
                <ShieldCheckIcon className="h-4 w-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-700">Role:</span>
                <span className="text-sm text-gray-900">
                  {selectedUser.is_admin ? 'Administrator' : 'Rower'}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <CalendarIcon className="h-4 w-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-700">Status:</span>
                <span
                  className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${selectedUser.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700'}`}
                >
                  {selectedUser.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-700">User ID:</span>
                <span className="text-sm text-gray-900">{selectedUser.id}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-700">Created:</span>
                <span className="text-sm text-gray-900">
                  {selectedUser.created_at
                    ? new Date(selectedUser.created_at).toLocaleDateString()
                    : 'N/A'}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-700">Last Updated:</span>
                <span className="text-sm text-gray-900">
                  {selectedUser.updated_at
                    ? new Date(selectedUser.updated_at).toLocaleDateString()
                    : 'N/A'}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-700">Last Login:</span>
                <span className="text-sm text-gray-900">
                  {selectedUser.last_login
                    ? new Date(selectedUser.last_login).toLocaleDateString()
                    : 'Never'}
                </span>
              </div>
            </div>
          </div>

          {selectedUser.training_groups && selectedUser.training_groups.length > 0 && (
            <div className="border-t border-gray-200 pt-4">
              <h3 className="mb-2 text-sm font-medium text-gray-700">
                Training Group Memberships:
              </h3>
              <div className="space-y-2">
                {selectedUser.training_groups.map((group) => (
                  <div
                    key={group.id}
                    className="flex items-center justify-between rounded-md border border-gray-200 bg-gray-50 px-3 py-2"
                  >
                    <div>
                      <span className="text-sm font-medium text-gray-900">{group.name}</span>
                      <span className="ml-2 text-xs text-gray-500">({group.role_in_group})</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {selectedUser.crews && selectedUser.crews.length > 0 && (
            <div className="border-t border-gray-200 pt-4">
              <h3 className="mb-2 text-sm font-medium text-gray-700">Crew Memberships:</h3>
              <div className="space-y-2">
                {selectedUser.crews.map((crew) => (
                  <div
                    key={crew.id}
                    className="flex items-center justify-between rounded-md border border-gray-200 bg-gray-50 px-3 py-2"
                  >
                    <div>
                      <span className="text-sm font-medium text-gray-900">{crew.name}</span>
                      <span className="ml-2 text-xs text-gray-500">({crew.boat_type})</span>
                    </div>
                    {crew.is_experimental && (
                      <span className="inline-flex items-center rounded-full bg-yellow-100 px-2.5 py-0.5 text-xs font-medium text-yellow-800">
                        Experimental
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="mt-6 flex justify-end">
          <button
            onClick={onClose}
            className="rounded-md bg-gray-600 px-4 py-2 text-sm font-medium text-white hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserDetailsModal;
